import { RegionSearchService } from '@immosky/object-search-filter';
import {
  Country,
  FilterOption,
  Region,
  RegionSearchResult,
} from '@immosky/types';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  SearchTermParser,
  SearchTermParserFactory,
} from './search-term-parser';

export class SearchBoxResultFinder {
  private searchTermParser: SearchTermParser;

  constructor(
    private regionSearchService: RegionSearchService,
    private country: Country
  ) {
    this.searchTermParser = new SearchTermParserFactory(this.country).create();
  }

  search(
    searchTerm: string
  ): Observable<FilterOption<RegionSearchResult | number>[]> {
    const result = this.searchTermParser.parse(searchTerm);

    switch (result.type) {
      case 'objectId':
        return this.searchForObject(result.value);
      case 'region':
        return this.searchForRegions(result.value);
      case 'ambiguous':
        return combineLatest([
          this.searchForObject(parseInt(result.value)),
          this.searchForRegions(result.value),
        ]).pipe(
          map(([objectResults, regionResults]) => {
            return [...objectResults, ...regionResults];
          })
        );
    }
  }

  private searchForObject(id: number): Observable<FilterOption<number>[]> {
    return this.regionSearchService.propertyNameById(id).pipe(
      map((x) => [x]),
      catchError(() => of([]))
    );
  }

  private searchForRegions(
    term: string
  ): Observable<FilterOption<RegionSearchResult>[]> {
    return this.regionSearchService.search(term).pipe(
      map((regions: RegionSearchResult[]) => {
        return regions.map((region: RegionSearchResult) => {
          let name = region.name;

          if (region.postalCodes?.length === 1) {
            name = `${region.name} (${region.postalCodes[0]})`;
          }

          return {
            text: name,
            value: region,
          } as FilterOption<RegionSearchResult>;
        });
      })
    );
  }
}
